import { Box, Chip, Paper, Tooltip } from '@mui/material'
import { DataGrid } from '@mui/x-data-grid'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import Filters from 'components/Filters'
import { useSession } from 'components/Providers'
import NoRecords from 'components/NoRecords'
import PersonSearch from 'components/PersonSearch'
import DateRangePicker from 'components/DateRangePicker/DateRangePicker'
import { getCampus, getNotificationTags } from 'store/lookup'
import { search } from 'store/notification/notification'
import {
    ALL_CAMPUSES,
    NOTIFICATION_CHANNEL_OPTIONS,
    NOTIFICATION_EVENT_GROUPS,
    NOTIFICATION_STATUS_OPTIONS,
    ROWS_PER_PAGE_OPTIONS,
} from 'constants'

const DEFAULT_PAGE_SIZE = 10

const getDefaultQuery = isProvider => ({
    channel: 'email',
    ...(isProvider && { campusId: ALL_CAMPUSES }),
    dateFrom: dayjs().startOf('day').toISOString(),
    dateTo: dayjs().endOf('day').toISOString(),
})

const statusColor = status => {
    if (status === 'failed') return 'error'
    if (status === 'pending') return 'warning'
    return 'success'
}

const rowId = (row, index) => row?.messageId ?? row?.id ?? `${row?.to ?? ''}|${row?.sentAt ?? ''}|${index}`

const eventGroup = (channel, event) => {
    if (!event) return null
    const groups = NOTIFICATION_EVENT_GROUPS[channel] ?? {}
    return Object.keys(groups).find(group => groups[group].includes(event)) ?? null
}

const ACRONYMS = new Set(['TTL', 'SMS', 'ID'])

const eventLabel = event =>
    String(event ?? '')
        .replace(/^TEXT_/, '')
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .split(/[_.\s]+/)
        .filter(Boolean)
        .map(word =>
            ACRONYMS.has(word.toUpperCase())
                ? word.toUpperCase()
                : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
        )
        .join(' ')

const listOf = value => {
    if (Array.isArray(value)) return value
    if (value instanceof Set) return [...value]
    return value ? [value] : []
}

const ERROR_FIELDS = ['error', 'errorMessage', 'diagnosticCode']

const hasValue = value => value !== undefined && value !== null && value !== ''

const getRowId = row => row.__rowId

const BASE_FIELDS = ['sentAt', 'to', 'subject', 'messageBody', 'campusId', 'tags', 'lastEvent', '__rowId']

const FIELD_LABELS = {
  messageId: 'Message ID',
  senderId: 'Sender ID',
  lastEventAt: 'Updated',
  messageStatusDescription: 'Status Description',
  providerMessageId: 'Provider Message ID',
}

const humanize = field =>
  FIELD_LABELS[field] ??
  String(field)
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_.]+/g, ' ')
    .replace(/^./, character => character.toUpperCase())

const looksLikeDate = field => /(^|[a-z])(At|Date)$/.test(field)

const displayValue = (field, value) => {
  if (value === null || value === undefined || value === '') return ''
  if (Array.isArray(value)) return value.join(', ')
  if (value instanceof Set) return [...value].join(', ')
  if (typeof value === 'object') return JSON.stringify(value)
  if (looksLikeDate(field)) {
    const parsed = dayjs(value)
    if (parsed.isValid()) return parsed.format('MM/DD/YYYY hh:mm A')
  }

  return String(value)
}


const NoNotifications = () => {
    const { t } = useTranslation()

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <NoRecords message={t('No notifications found for these filters')} />
        </Box>
    )
}

const GRID_SLOTS = { noRowsOverlay: NoNotifications }
const GRID_SLOT_PROPS = { loadingOverlay: { variant: 'circular-progress', noRowsVariant: 'circular-progress' } }
const GRID_SX = {
    height: 'calc(100vh - 305px)',
    overflow: 'auto',
    '--DataGrid-overlayHeight': '200px',
    '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center' },
}

export default function NotificationReport() {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const { district } = useSession()
    const isProvider = Boolean(district?.isServiceProvider)

    const [query, setQuery] = useState(() => getDefaultQuery(isProvider))
    const [sender, setSender] = useState(null)
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: DEFAULT_PAGE_SIZE })
    const [columnChoices, setColumnChoices] = useState({})

    const list = useSelector(state => state.notification.notification.list)
    const loading = useSelector(state => state.notification.notification.searchLoading)
    const campus = useSelector(state => state.lookup.campus)
    const notificationTags = useSelector(state => state.lookup.notificationTags)

    useEffect(() => {
        if (isProvider && !campus) dispatch(getCampus())
    }, [dispatch, isProvider, Boolean(campus)])

    useEffect(() => {
        if (!notificationTags) dispatch(getNotificationTags())
    }, [dispatch, Boolean(notificationTags)])

    const queryKey = JSON.stringify(query)

    useEffect(() => {
        dispatch(search(query))
        setPaginationModel(model => ({ ...model, page: 0 }))
    }, [dispatch, queryKey])

    const campusOptions = useMemo(
        () => [
            { value: ALL_CAMPUSES, label: t('All Campuses') },
            ...(campus ?? []).map(item => ({ value: item.id, label: item.name })),
        ],
        [campus, t],
    )

    const tagOptions = useMemo(
        () => [...new Set(notificationTags ?? [])].map(tag => ({ value: tag, label: tag })),
        [notificationTags],
    )

    const rows = useMemo(
        () =>
            (list ?? [])
                .map((row, index) => ({ ...row, __rowId: rowId(row, index) }))
                .sort((a, b) => String(b.sentAt ?? '').localeCompare(String(a.sentAt ?? ''))),
        [list],
    )

    const campusName = useCallback(
        id => {
            if (id === undefined || id === null || id === '') return ''
            if (Number(id) === ALL_CAMPUSES) return t(' ')

            return (campus ?? []).find(item => Number(item.id) === Number(id))?.name ?? String(id)
        },
        [campus, t],
    )

    const presentFields = useMemo(() => {
        const found = new Set()

        for (const row of list ?? []) {
            for (const field of Object.keys(row ?? {})) {
                if (!found.has(field) && hasValue(row[field])) found.add(field)
            }
        }

        return [...found].sort().join(',')
    }, [list])

    const optionalColumns = useMemo(() => {
        const found = new Set(presentFields ? presentFields.split(',') : [])

        const optional = []

        for (const field of [...found].filter(name => !BASE_FIELDS.includes(name))) {
            optional.push({
                field,
                headerName: t(humanize(field)),
                flex: 1,
                minWidth: 150,
                sortable: !ERROR_FIELDS.includes(field),
                valueGetter: (value, row) => displayValue(field, row?.[field]),
            })
        }

        return optional
    }, [presentFields, t])


    const columnVisibility = useMemo(
        () => ({
            ...Object.fromEntries(optionalColumns.map(column => [column.field, false])),
            ...columnChoices,
        }),
        [optionalColumns, columnChoices],
    )

    const handleDateRange = useCallback(range => {
        setQuery(prev => ({
            ...prev,
            dateFrom: range[0].startOf('day').toISOString(),
            dateTo: range[1].endOf('day').toISOString(),
        }))
    }, [])

    const handleSender = useCallback(person => {
        setSender(person)
        setQuery(prev => {
            const next = { ...prev }
            if (person?.id) next.senderId = person.id
            else delete next.senderId
            return next
        })
    }, [])

    const handleChange = next => {
        const defaults = getDefaultQuery(isProvider)
        const { campusId, ...rest } = next
        setQuery({
            ...rest,
            channel: next.channel || defaults.channel,
            ...(isProvider && { campusId: campusId ?? defaults.campusId }),
            dateFrom: next.dateFrom || defaults.dateFrom,
            dateTo: next.dateTo || defaults.dateTo,
        })
    }

    const filtersConfig = useMemo(
        () => [
            {
                key: 'channel',
                label: t('Type'),
                type: 'autocomplete',
                options: NOTIFICATION_CHANNEL_OPTIONS.map(option => ({ ...option, label: t(option.label) })),
                minClearLength: 1,
                gridProps: { xs: 12, sm: 6, md: 1 },
            },
            ...(isProvider
                ? [
                      {
                          key: 'campusId',
                          label: t('Campus'),
                          type: 'autocomplete',
                          options: campusOptions,
                          gridProps: { xs: 12, sm: 6, md: 2 },
                          minClearLength: 1,
                      },
                  ]
                : []),
            {
                key: 'senderId',
                type: 'custom',
                gridProps: { xs: 12, sm: 6, md: 2 },
                render: () => <PersonSearch label={t('Sender')} value={sender} onChange={handleSender} />,
            },
            {
                key: 'search',
                label: t('Search'),
                type: 'text',
                placeholder:
                    query.channel === 'sms' ? t('Search by Receiver or Message') : t('Search by Receiver or Subject'),
                gridProps: { xs: 12, sm: 6, md: 2 },
            },
            {
                key: 'tags',
                label: t('Tag'),
                type: 'autocomplete',
                options: tagOptions,
                gridProps: { xs: 12, sm: 6, md: 1 },
            },
            {
                key: 'status',
                label: t('Status'),
                type: 'autocomplete',
                options: NOTIFICATION_STATUS_OPTIONS.map(option => ({ ...option, label: t(option.label) })),
                gridProps: { xs: 12, sm: 6, md: 1 },
            },
            {
                key: 'dateRange',
                type: 'custom',
                gridProps: { xs: 12, sm: 6, md: 3 },
                render: ({ query: current }) => (
                    <DateRangePicker
                        label={t('Date Range')}
                        startDate={current?.dateFrom}
                        endDate={current?.dateTo}
                        setDateRange={handleDateRange}
                        showQuickSelect
                    />
                ),
            },
        ],
        [t, isProvider, campusOptions, tagOptions, handleDateRange, handleSender, sender, query.channel],
    )

    const columns = useMemo(
        () => [
            {
                field: 'sentAt',
                headerName: t('Sent'),
                flex: 1,
                minWidth: 160,
                valueGetter: (value, row) => row?.sentAt ?? '',
                renderCell: ({ row }) => (row?.sentAt ? dayjs(row.sentAt).format('MM/DD/YYYY hh:mm A') : ''),
            },
            {
                field: 'to',
                headerName: t('Receiver'),
                flex: 1.2,
                minWidth: 180,
                valueGetter: (value, row) => row?.to ?? '',
            },
            {
                field: 'content',
                headerName: query.channel === 'sms' ? t('Message') : t('Subject'),
                flex: 1.8,
                minWidth: 220,
                valueGetter: (value, row) => (query.channel === 'sms' ? row?.messageBody : row?.subject) ?? '',
                renderCell: ({ value }) => (
                    <Tooltip title={value || ''} placement="top-start">
                        <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</Box>
                    </Tooltip>
                ),
            },
            ...(isProvider
                ? [
                      {
                          field: 'campusId',
                          headerName: t('Campus'),
                          flex: 1,
                          minWidth: 150,
                          valueGetter: (value, row) => campusName(row?.campusId),
                      },
                  ]
                : []),
            {
                field: 'tags',
                headerName: t('Tags'),
                flex: 1,
                minWidth: 140,
                sortable: false,
                valueGetter: (value, row) => listOf(row?.tags).join(', '),
            },
            {
                field: 'lastEvent',
                headerName: t('Status'),
                width: 150,
                valueGetter: (value, row) => row?.lastEvent ?? '',
                renderCell: ({ row }) => {
                    if (!row?.lastEvent) return ''
                    const group = eventGroup(query.channel, row.lastEvent)

                    return (
                        <Tooltip title={row.lastEvent} placement="top">
                            <Chip
                                label={t(eventLabel(row.lastEvent))}
                                color={group ? statusColor(group) : 'default'}
                                variant={group ? 'filled' : 'outlined'}
                                size="small"
                            />
                        </Tooltip>
                    )
                },
            },
            ...optionalColumns,
        ],
        [t, isProvider, query.channel, campusName, optionalColumns],
    )

    return (
        <Box>
            <Paper>
                <Filters
                    filtersConfig={filtersConfig}
                    values={query}
                    onChange={handleChange}
                    gridContainerProps={{ sx: { p: 1, width: '100%', flexWrap: 'wrap', mt: 1 } }}
                />
            </Paper>

            <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden', p: 1, mt: 1 }}>
                <DataGrid
                    rows={rows}
                    columns={columns}
                    getRowId={getRowId}
                    loading={loading}
                    paginationMode="client"
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    pageSizeOptions={ROWS_PER_PAGE_OPTIONS}
                    columnVisibilityModel={columnVisibility}
                    onColumnVisibilityModelChange={setColumnChoices}
                    slots={GRID_SLOTS}
                    slotProps={GRID_SLOT_PROPS}
                    sx={GRID_SX}
                />
            </Paper>

        </Box>
    )
}
